// components/Calendar/views/MonthView.jsx
import React, { useCallback } from 'react';
import { getAppointmentTypeClass, getHolidaysForDay } from '../../../utils/appointmentUtils';
import { AppointmentServiceUtils } from '../../../utils/appointmentUtils';
import { UI_CONSTANTS, SCROLL_CONFIG } from '../../../constants/calendar';
import { isToday as isTodayUtil, formatTime } from '../../../utils/dateUtils';

const MonthView = ({
  viewDays,
  appointments,
  holidays,
  currentDate,
  onDayClick,
  onMonthChange,
  currentOfficeStaff
}) => {
  const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 768);
  const [isTransitioning, setIsTransitioning] = React.useState(false);
  const [selectedTypes, setSelectedTypes] = React.useState(new Set());
  const [selectedStylists, setSelectedStylists] = React.useState(new Set());

  // Smooth scroll state - for visual feedback during scroll
  const [scrollProgress, setScrollProgress] = React.useState(0); // -1 to 1, indicates scroll direction progress

  // Refs
  const scrollTimeoutRef = React.useRef(null);
  const monthViewRef = React.useRef(null);
  const containerRef = React.useRef(null);
  const accumulatedDelta = React.useRef(0);
  const touchStartRef = React.useRef({ y: 0, time: 0 });
  const isNavigating = React.useRef(false);

  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Threshold for triggering month change (accumulated scroll pixels)
  const SCROLL_THRESHOLD = 200;

  // Navigate to next/previous month with animation
  const navigateMonth = useCallback((direction) => {
    if (isNavigating.current || !onMonthChange) return;

    isNavigating.current = true;
    setIsTransitioning(true);

    // Trigger navigation
    onMonthChange(direction);

    // Reset state after animation completes
    setTimeout(() => {
      setIsTransitioning(false);
      setScrollProgress(0);
      accumulatedDelta.current = 0;
      isNavigating.current = false;
    }, 300);
  }, [onMonthChange]);

  // Wheel handler - accumulates scroll and triggers navigation at threshold
  React.useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleWheel = (e) => {
      if (!onMonthChange || isNavigating.current) return;

      // On mobile, allow native scroll first
      if (isMobile) {
        const atTop = container.scrollTop === 0;
        const atBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - 5;

        // Only intercept scroll at boundaries
        if ((e.deltaY < 0 && !atTop) || (e.deltaY > 0 && !atBottom)) {
          return; // Let native scroll handle it
        }
      }

      e.preventDefault();

      // Accumulate scroll delta
      accumulatedDelta.current += e.deltaY * SCROLL_CONFIG.SCROLL_SENSITIVITY;

      // Update visual progress (clamped to -1 to 1)
      const progress = Math.max(-1, Math.min(1, accumulatedDelta.current / SCROLL_THRESHOLD));
      setScrollProgress(progress);

      // Clear existing timeout
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      // Check if threshold reached - navigate immediately
      if (Math.abs(accumulatedDelta.current) >= SCROLL_THRESHOLD) {
        const direction = accumulatedDelta.current > 0 ? 1 : -1;
        navigateMonth(direction);
      } else {
        // Set timeout to reset scroll progress if user stops scrolling
        scrollTimeoutRef.current = setTimeout(() => {
          // Snap back - didn't reach threshold
          setScrollProgress(0);
          accumulatedDelta.current = 0;
        }, SCROLL_CONFIG.DATA_FETCH_DEBOUNCE_MS);
      }
    };

    // Use passive: false to allow preventDefault on desktop
    container.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      container.removeEventListener('wheel', handleWheel);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [onMonthChange, isMobile, navigateMonth]);

  // Touch handlers for mobile swipe navigation
  const handleTouchStart = useCallback((e) => {
    touchStartRef.current = {
      y: e.touches[0].clientY,
      time: Date.now()
    };
    accumulatedDelta.current = 0;
  }, []);

  const handleTouchEnd = useCallback((e) => {
    const touchEndY = e.changedTouches[0].clientY;
    const deltaY = touchStartRef.current.y - touchEndY;
    const timeDelta = Date.now() - touchStartRef.current.time;
    const velocity = Math.abs(deltaY / timeDelta);

    // Fast swipe or sufficient distance triggers navigation
    if ((velocity > 0.5 && Math.abs(deltaY) > 50) || Math.abs(deltaY) > SCROLL_THRESHOLD) {
      const direction = deltaY > 0 ? 1 : -1;
      navigateMonth(direction);
    } else {
      // Snap back
      setScrollProgress(0);
      accumulatedDelta.current = 0;
    }
  }, [navigateMonth]);

  // Split viewDays into weeks for rendering
  const weeks = React.useMemo(() => {
    const result = [];
    for (let i = 0; i < viewDays.length; i += UI_CONSTANTS.MONTH_DAYS_PER_WEEK) {
      result.push(viewDays.slice(i, i + UI_CONSTANTS.MONTH_DAYS_PER_WEEK));
    }
    return result;
  }, [viewDays]);

  const handleDayClick = (day) => {
    onDayClick(day);
  };

  // Toggle appointment type filter
  const toggleTypeFilter = (className) => {
    setSelectedTypes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(className)) {
        newSet.delete(className);
      } else {
        newSet.add(className);
      }
      return newSet;
    });
  };

  // Toggle stylist filter
  const toggleStylistFilter = (stylistId) => {
    setSelectedStylists(prev => {
      const newSet = new Set(prev);
      if (newSet.has(stylistId)) {
        newSet.delete(stylistId);
      } else {
        newSet.add(stylistId);
      }
      return newSet;
    });
  };

  // Clear all filters
  const clearAllFilters = () => {
    setSelectedTypes(new Set());
    setSelectedStylists(new Set());
  };

  // Filter appointments based on selected types and stylists
  const filterAppointments = (apts) => {
    return apts.filter(apt => {
      // If no filters are active, show all
      if (selectedTypes.size === 0 && selectedStylists.size === 0) {
        return true;
      }

      const typeClass = getAppointmentTypeClass(apt.Title);
      const typeMatch = selectedTypes.size === 0 || selectedTypes.has(typeClass);
      const stylistMatch = selectedStylists.size === 0 || selectedStylists.has(apt.MWuserID);

      // Show appointment if it matches both filters (AND logic)
      return typeMatch && stylistMatch;
    });
  };

  // Define appointment types legend
  const appointmentTypes = [
    { name: 'Customer Pickup', className: 'customer-pickup' },
    { name: 'New Cut', className: 'new-cut' },
    { name: 'Reminder', className: 'reminder' },
    { name: 'Lunch', className: 'lunch' },
    { name: 'Re-Cut', className: 're-cut' },
    { name: 'Consultation', className: 'consultation' },
    { name: 'Special Occasion', className: 'special-occasion' },
    { name: 'Wash and Set', className: 'wash-and-set' },
    { name: 'Send Out', className: 'send-out' },
    { name: 'Fitting', className: 'fitting' },
    { name: 'Repair', className: 'repair' },
    { name: 'Touch-Up', className: 'touch-up' },
    { name: 'Virtual Appointment', className: 'virtual-appointment' }
  ];

  const hasActiveFilters = selectedTypes.size > 0 || selectedStylists.size > 0;

  return (
    <div className="month-view" ref={monthViewRef}>
    
      {/* Stylist Filters */}
      {currentOfficeStaff && currentOfficeStaff.length > 0 && (
        <div className="filter-section stylist-filters">
          <div className="filter-items">
            {currentOfficeStaff.map((stylist) => (
              <div
                key={stylist.id}
                className={`filter-item stylist-filter-item ${selectedStylists.has(stylist.id) ? 'active' : ''}`}
                onClick={() => toggleStylistFilter(stylist.id)}
              >
                <div className="stylist-avatar">{stylist.name.charAt(0)}</div>
                <span className="filter-label">{stylist.name}</span>
                {selectedStylists.has(stylist.id) && <span className="filter-checkmark">✓</span>}
              </div>
            ))}
                 {/* Clear Filters Button */}
      {hasActiveFilters && (
    
          <button className="mv-clear-filters-btn" onClick={clearAllFilters}>
            Clear All Filters ({selectedTypes.size + selectedStylists.size})
          </button>
    
      )}
          </div>
        </div>
      )}
  
      {/* Appointment Types Legend/Filter */}
      <div className="filter-section appointment-legend">
        <div className="legend-items">
          {appointmentTypes.map((type) => (
            <div
              key={type.className}
              className={`legend-item filter-item ${selectedTypes.has(type.className) ? 'active' : ''}`}
              onClick={() => toggleTypeFilter(type.className)}
            >
              <div className={`legend-color-box ${type.className}`}></div>
              <span className="legend-label">{type.name}</span>
              {selectedTypes.has(type.className) && <span className="filter-checkmark">✓</span>}
            </div>
          ))}
        </div>
      </div>

 
      <div
        ref={containerRef}
        className={`month-calendar ${isMobile ? 'mobile-month-calendar' : ''} ${isTransitioning ? 'transitioning' : ''} ${scrollProgress !== 0 ? 'scrolling' : ''}`}
        onTouchStart={isMobile ? handleTouchStart : undefined}
        onTouchEnd={isMobile ? handleTouchEnd : undefined}
      >
        {/* Month header with day names - stays fixed */}
        <div className="month-header">
          {(isMobile ? ['S', 'M', 'T', 'W', 'T', 'F', 'S'] : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']).map((day, index) => (
            <div key={index} className="month-day-header">{day}</div>
          ))}
        </div>

        {/* Month grid with smooth scroll feedback */}
        <div
          className={`month-grid ${isMobile ? 'mobile-month-grid' : ''}`}
          style={{
            transform: `translateY(${-scrollProgress * 30}px)`,
            opacity: 1 - Math.abs(scrollProgress) * 0.3,
          }}
        >
          {weeks.map((week, weekIndex) => (
            week.map((day, dayIndex) => {
              const allDayAppointments = appointments.filter(apt =>
                AppointmentServiceUtils.doesAppointmentSpanDate(apt, day)
              );
              const dayAppointments = filterAppointments(allDayAppointments);
              const dayHolidays = getHolidaysForDay(holidays, day);
              const isToday = isTodayUtil(day);
              const isCurrentMonth = day.getMonth() === currentDate.getMonth();
              
              return (
                <div
                  key={`${weekIndex}-${dayIndex}`}
                  className={`month-day-cell ${isToday ? 'today' : ''} ${!isCurrentMonth ? 'other-month' : ''} ${dayHolidays.length > 0 ? 'has-holiday' : ''} ${isMobile ? 'mobile-day-cell' : ''}`}
                  onClick={() => handleDayClick(day)}
                >
                  {/* Day number and holiday indicator */}
                  <div className="month-day-number">
                    {day.getDate()}
                    {dayHolidays.length > 0 && (
                      <span 
                        className="month-holiday-dot" 
                        title={dayHolidays.map(h => h.HolidayName).join(', ')}
                      >
                        🕎
                      </span>
                    )}
                  </div>
                  
                  {/* Appointment count badge */}
                  <div className="month-appointments-count">
                    {dayAppointments.length > 0 && (
                      <span className={`appointment-badge ${isMobile ? 'mobile-appointment-badge' : ''}`}>
                        {dayAppointments.length}
                      </span>
                    )}
                  </div>
                  
                  {/* Appointment previews - simplified on mobile */}
                  <div className="month-day-appointments">
                    {!isMobile ? (
                      // Desktop: Show both appointment details AND dots
                      <>
                        {/* Appointment dots for quick visual reference */}
                        <div className="desktop-appointment-dots">
                          {dayAppointments.slice(0, 12).map((appointment) => (
                            <div
                              key={`dot-${appointment.ID}`}
                              className={`desktop-appointment-dot ${getAppointmentTypeClass(appointment.Title)}`}
                              title={`${appointment.ClientName} - ${appointment.Title} at ${formatTime(appointment.EventStart)}`}
                            />
                          ))}
                          {dayAppointments.length > 12 && (
                            <div className="desktop-more-dots">+{dayAppointments.length - 12}</div>
                          )}
                        </div>
                        
                        {/* Traditional appointment previews (fewer shown due to dots) */}
                        {dayAppointments.slice(0, Math.min(2, UI_CONSTANTS.MAX_MONTH_APPOINTMENTS_DISPLAY)).map((appointment) => (
                          <div
                            key={appointment.ID}
                            className={`month-appointment ${getAppointmentTypeClass(appointment.Title)}`}
                            title={`${appointment.ClientName} - ${appointment.Title} at ${new Date(appointment.EventStart).toLocaleTimeString('en-US', { 
                              hour: 'numeric', 
                              minute: '2-digit',
                              hour12: true 
                            })}`}
                          >
                            <span className="month-appointment-client">{appointment.ClientName}</span>
                            <span className="month-appointment-time">
                              {new Date(appointment.EventStart).toLocaleTimeString('en-US', { 
                                hour: 'numeric', 
                                minute: '2-digit',
                                hour12: true 
                              })}
                            </span>
                          </div>
                        ))}
                        
                        {/* Show "more" indicator if there are additional appointments */}
                        {dayAppointments.length > 2 && (
                          <div className="more-appointments">
                            +{dayAppointments.length - 2} more appointments
                          </div>
                        )}
                      </>
                    ) : (
                      // Mobile: Show simplified dots for appointments (existing code)
                      dayAppointments.length > 0 && (
                        <div className="mobile-appointment-dots">
                          {dayAppointments.slice(0, 3).map((appointment) => (
                            <div
                              key={appointment.ID}
                              className={`mobile-appointment-dot ${getAppointmentTypeClass(appointment.Title)}`}
                              title={`${appointment.ClientName} - ${appointment.Title}`}
                            />
                          ))}
                          {dayAppointments.length > 3 && (
                            <div className="mobile-more-dots">...</div>
                          )}
                        </div>
                      )
                    )}
                  </div>
                  
                  {/* Mobile tap indicator */}
                  {/* {isMobile && (
                    <div className="mobile-tap-indicator">
                      <span>Tap for details</span>
                    </div>
                  )} */}
                </div>
              );
            })
          ))}
        </div>
      </div>

      {/* Scroll indicator - shows during scroll */}
      {scrollProgress !== 0 && !isMobile && (
        <div className="month-scroll-indicator visible">
          <div
            className="month-scroll-indicator-thumb"
            style={{
              transform: `translateY(${scrollProgress * 25 + 25}px)`
            }}
          />
        </div>
      )}
    </div>
  );
};

export default MonthView;